import { Separator } from "@base-ui/react"
import { authIdentity } from "@defuse-protocol/internal-utils"
import { ChevronRightIcon, UserPlusIcon } from "@heroicons/react/20/solid"
import {
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline"
import { UserIcon } from "@heroicons/react/24/solid"
import { getAccountLabel } from "@src/app/(app)/(dashboard)/settings/actions"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { GiftIcon } from "@src/icons"
import { useActivityDock } from "@src/providers/ActivityDockProvider"
import { useBootstrapActor } from "@src/providers/AppBootstrapProvider"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import { logger } from "@src/utils/logger"
import { useQuery } from "@tanstack/react-query"
import Link from "next/link"
import { type ComponentType, useCallback, useContext, useState } from "react"
import { AdaptiveOverlay } from "./AdaptiveOverlay"
import { AddressOverlay } from "./AddressOverlay"
import Button from "./Button"

const UserMenuMobile = () => {
  const [open, setOpen] = useState(false)
  const [addressOpen, setAddressOpen] = useState(false)
  const { state, signOut } = useConnectWallet()
  const { clearDockItems } = useActivityDock()
  const flags = useContext(FeatureFlagsContext)
  const bootstrapActor = useBootstrapActor()

  const closeMenu = useCallback(() => {
    setOpen(false)
    setAddressOpen(false)
  }, [])

  const internalAddress =
    state.address != null && state.chainType != null
      ? authIdentity.authHandleToIntentsUserId(state.address, state.chainType)
      : undefined

  const { data: accountLabel } = useQuery({
    queryKey: ["account-label", internalAddress],
    queryFn: async () => {
      const result = await getAccountLabel()
      if (!result.ok) return null
      return result.value
    },
    enabled: internalAddress != null && state.isAuthorized,
  })

  const displayLabel = accountLabel || "Account"

  const links = [
    {
      label: "Settings",
      href: "/settings",
      icon: Cog6ToothIcon,
    },
    {
      label: "Support",
      onClick: () => {
        if (typeof window !== "undefined" && window.Beacon) {
          window.Beacon("navigate", "/ask/message/")
          window.Beacon("open")
        }
      },
      icon: QuestionMarkCircleIcon,
    },
    {
      label: "Help articles",
      onClick: () => {
        if (typeof window !== "undefined" && window.Beacon) {
          window.Beacon("navigate", "/answers/")
          window.Beacon("open")
        }
      },
      icon: BookOpenIcon,
    },
    {
      label: "Terms",
      href: "/terms",
      icon: DocumentTextIcon,
    },
    {
      label: "Sign out",
      "data-testid": "sign-out-button",
      onClick: async () => {
        try {
          if (state.chainType) {
            clearDockItems()
            bootstrapActor.send({ type: "SIGN_OUT" })
            await signOut({ id: state.chainType })
          }
        } catch (err) {
          logger.error(err)
        }
      },
      icon: ArrowRightStartOnRectangleIcon,
    },
  ]

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-3 rounded-xl text-left"
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-green-500">
          <UserIcon className="size-6 text-gray-900" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="max-w-27 truncate font-semibold text-base/5 text-gray-900 tracking-tight dark:text-white">
            {displayLabel}
          </div>
        </div>
      </button>

      <AdaptiveOverlay
        open={open}
        onClose={closeMenu}
        desktop={null}
        mobile={{
          type: "drawer",
          props: {
            header: (
              <div className="flex w-full items-center gap-3 pt-4">
                <div className="flex size-13 shrink-0 items-center justify-center rounded-[16px] bg-green-500">
                  <UserIcon className="size-6.5 text-gray-900" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-gray-900 text-lg/6 tracking-tight dark:text-white">
                    {displayLabel}
                  </div>
                </div>

                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setAddressOpen(true)}
                >
                  View address
                </Button>
              </div>
            ),
          },
        }}
      >
        <Separator
          orientation="horizontal"
          className="my-2 h-px w-full bg-gray-200 dark:bg-white/10"
        />

        {!flags.isReferralDisabled && (
          <Link href="/referrals" className="flex items-center gap-3 py-4">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-white/10">
              <UserPlusIcon className="size-5 text-gray-900 dark:text-white" />
            </div>

            <div className="flex flex-1 flex-col">
              <div className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
                Refer a friend
              </div>
              <div className="font-medium text-gray-500 text-sm/4 dark:text-gray-400">
                Earn when they trade
              </div>
            </div>

            <ChevronRightIcon className="size-5 shrink-0 text-gray-900 dark:text-white" />
          </Link>
        )}

        <Link href="/gifts" className="flex items-center gap-3 py-4">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-white/10">
            <GiftIcon className="size-5 text-gray-900 dark:text-white" />
          </div>

          <div className="flex flex-1 flex-col">
            <div className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
              Give a gift
            </div>
            <div className="font-medium text-gray-500 text-sm/4 dark:text-gray-400">
              Surprise someone with a gift
            </div>
          </div>

          <ChevronRightIcon className="size-5 shrink-0 text-gray-900 dark:text-white" />
        </Link>

        <Separator
          orientation="horizontal"
          className="my-2 h-px w-full bg-gray-200 dark:bg-white/10"
        />

        {links.map((link) => (
          <MenuItem key={link.label} {...link} />
        ))}

        <AddressOverlay
          open={addressOpen}
          onClose={() => setAddressOpen(false)}
        />
      </AdaptiveOverlay>
    </>
  )
}

export default UserMenuMobile

const MenuItem = ({
  icon: Icon,
  label,
  href,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  href?: string
  onClick?: () => void
}) => {
  const classes =
    "text-left flex gap-3 items-center py-4 text-gray-900 w-full dark:text-white"

  const content = (
    <>
      <Icon className="size-6 shrink-0" />
      <span className="flex-1 font-semibold text-base/6 tracking-tight">
        {label}
      </span>
      <ChevronRightIcon className="size-5 shrink-0" />
    </>
  )

  if (href) {
    return (
      <Link href={href} className={classes}>
        {content}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick}>
        {content}
      </button>
    )
  }

  return null
}
